import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CalendarCheck, ChevronRight, Flag, Menu, Users, Snowflake, type LucideIcon } from 'lucide-react';
import { challengePhase, challengeToday } from '@radevents/shared';
import { useAuth } from '../../contexts/AuthContext';
import { PERMANENT_LANDING } from '../../config/seoLandings';
import { useChallengeSummaryState } from '../WinterchallengePromoBanner';
import { SOCIAL_LINKS, InstagramGlyph, FacebookGlyph } from '../SocialIcons';
import { PFLICHT_LINKS } from './pflichtLinks';
import { useNeueNews } from '../../hooks/useKopfHinweise';

// Tab-Leiste der Handy-Webseite: Events · News · Meine Events · Profil · Mehr. Profis, Veranstalter
// und die permanenten Events liegen seit dem 13.10.2026 unter „Mehr" — auf der Startseite standen
// dafür ein Link und ein eigener Umschalter über der Liste, die den ersten Bildschirm füllten.
// Präfix-Matching, damit auch Unterseiten (News-Artikel, Rennkalender-Details/-Etappen,
// Veranstalter-Profile, Info-Seiten) mobil die Tab-Bar behalten statt des Desktop-Footers.
// Event-Details (/events/:id) bleiben bewusst außen vor — dort liegt unten die eigene
// Aktionsleiste (app-bottombar), die mit der Tab-Bar kollidieren würde.
const SHOW_ON_PREFIXES = [
  `/${PERMANENT_LANDING.slug}`, '/rennkalender', '/radsport-im-tv', '/veranstalter', '/news', '/favorites', '/profile',
  '/fuer-partner', '/ueber-uns', '/kontakt', '/impressum', '/datenschutz', '/winterchallenge', '/freunde',
];

const TABS = [
  {
    label: 'Events',
    path: '/',
    match: (p: string) => p === '/',
    icon: (active: boolean) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 2}
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
        <rect x="3" y="4.5" width="18" height="16" rx="2.5" />
        <path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
      </svg>
    ),
  },
  {
    label: 'News',
    path: '/news',
    match: (p: string) => p.startsWith('/news'),
    icon: (active: boolean) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 2}
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
        <path d="M4 5h13v14H5a1 1 0 0 1-1-1z" />
        <path d="M17 8h3v9a2 2 0 0 1-2 2" />
        <path d="M7 9h7M7 12.5h7M7 16h4" />
      </svg>
    ),
  },
  {
    label: 'Meine Events',
    path: '/favorites',
    match: (p: string) => p.startsWith('/favorites'),
    icon: (active: boolean) => <CalendarCheck size={22} strokeWidth={active ? 2.2 : 2} />,
  },
  {
    label: 'Profil',
    path: '/profile',
    match: (p: string) => p.startsWith('/profile'),
    icon: (active: boolean) => (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={active ? 2.2 : 2}
        strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22 }}>
        <circle cx="12" cy="8" r="3.6" />
        <path d="M5.2 20c.6-3.5 3.5-5.5 6.8-5.5S18.2 16.5 18.8 20" />
      </svg>
    ),
  },
];

/** Einträge unter „Mehr". Ein neuer Bereich kommt hierher, nicht als sechster Tab. */
const MORE_ITEMS: { label: string; path: string; icon: LucideIcon; match: (p: string) => boolean; seasonal?: boolean }[] = [
  { label: 'Veranstalter', path: '/veranstalter', icon: Users,
    match: (p: string) => p.startsWith('/veranstalter') && !p.startsWith('/veranstalter-bereich') },
  { label: 'Profi-Rennen', path: '/rennkalender', icon: Flag,
    match: (p: string) => p.startsWith('/rennkalender') || p === '/radsport-im-tv' },
  // „Permanente Events“ stand hier bis zum 28.10.2026; seitdem führt „Jederzeit fahrbar“ im
  // Zeitraum-Filter dorthin, ein zweiter Weg zum selben Ziel war zu viel (386 Sitzungen in 90 Tagen,
  // fast alle aus diesem Eintrag).
  // Saisonal (docs/bereiche/winterchallenge.md): kein eigener Tab, ein Eintrag hier, nur solange
  // die Saison bevorsteht oder läuft (`seasonal`, siehe unten).
  { label: 'Winterchallenge', path: '/winterchallenge', icon: Snowflake,
    match: (p: string) => p.startsWith('/winterchallenge'), seasonal: true },
];

export default function BottomTabBar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  // Wie im Kopf: der Eintrag verschwindet nach dem letzten Tag der Saison von selbst.
  const { summary: challenge } = useChallengeSummaryState();
  const challengeActive = challenge != null && challengePhase(challenge, challengeToday()) !== 'after';
  const moreItems = MORE_ITEMS.filter(item => !item.seasonal || challengeActive);

  // Nach dem Seitenwechsel ist das Blatt zu, auch wenn er nicht über einen Eintrag kam.
  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMoreOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [moreOpen]);

  const neueNews = useNeueNews();
  const path = location.pathname;
  const visible = path === '/' || SHOW_ON_PREFIXES.some(p => path === p || path.startsWith(`${p}/`));
  if (!visible) return null;

  const handleTab = (path: string) => {
    // „Meine Events" hat eine eigene Ansicht für Gäste (MeineEventsGast), wie am Rechner. Nur das
    // Profil braucht ein Konto.
    if (path === '/profile' && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: path } } });
      return;
    }
    navigate(path);
  };

  const moreActive = moreItems.some(item => item.match(path));

  return (
    <>
    {moreOpen && (
      <>
        <div className="bottom-more-backdrop" onClick={() => setMoreOpen(false)} />
        <div id="bottom-more-sheet" className="bottom-more-sheet" role="dialog" aria-label="Mehr">
          {moreItems.map(({ label, path: to, icon: Icon, match }) => (
            <Link key={to} to={to} className={`bottom-more-item${match(path) ? ' active' : ''}`}
              aria-current={match(path) ? 'page' : undefined}>
              <Icon size={20} aria-hidden />
              <span>{label}</span>
              <ChevronRight size={16} aria-hidden className="bottom-more-chevron" />
            </Link>
          ))}
          {/* Unten eine graue Zeile wie bei LinkedIn (28.10.2026): auf dem Handy gibt es keine Fußzeile,
              ohne sie wären Impressum und Datenschutz dort nicht erreichbar. „Für Veranstalter“ steht
              als Hinweis im Verzeichnis, „Für Partner“ hängt an der Anzeige „Hier werben“; beide
              kamen in 90 Tagen auf unter 100 Sitzungen. */}
          <div className="bottom-more-fuss">
            {PFLICHT_LINKS.map(l => <Link key={l.to} to={l.to}>{l.label}</Link>)}
            <span className="bottom-more-sozial">
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Radsport Events auf Instagram" title="Instagram"><InstagramGlyph size={17} strokeWidth={1.8} /></a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Radsport Events auf Facebook" title="Facebook"><FacebookGlyph size={17} /></a>
            </span>
          </div>
        </div>
      </>
    )}
    <nav className="bottom-tab-bar">
      {TABS.map((tab) => {
        const isActive = tab.match(location.pathname);
        return (
          <button
            key={tab.path}
            className={`bottom-tab-item${isActive ? ' active' : ''}`}
            onClick={() => handleTab(tab.path)}
          >
            <span className="bottom-tab-icon">
              {tab.icon(isActive)}
              {/* Neuer Artikel in den letzten drei Tagen (useNeueNews), wie im Kopf am Rechner. */}
              {tab.path === '/news' && neueNews && <span className="bottom-tab-punkt" aria-label="neuer Artikel" />}
            </span>
            <span className="bottom-tab-label">{tab.label}</span>
          </button>
        );
      })}
      <button
        type="button"
        className={`bottom-tab-item${moreActive || moreOpen ? ' active' : ''}`}
        onClick={() => setMoreOpen(open => !open)}
        aria-expanded={moreOpen}
        aria-controls="bottom-more-sheet"
      >
        <Menu size={22} strokeWidth={moreActive || moreOpen ? 2.2 : 2} aria-hidden />
        <span className="bottom-tab-label">Mehr</span>
      </button>
    </nav>
    </>
  );
}
